import { AuthContext } from '@repo-level/core-front';
import { createScript } from 'helpers/common';
import { CustomerIOEvents } from 'helpers/customerio';
import { AB_TESTS } from 'modules/abTesting/constants';
import { useABTestingV2 } from 'modules/abTesting/hooks/useABTesting';
import { canAssignMission } from 'modules/missions/helpers/mission';
import { useAssignMission } from 'modules/missions/hooks/useAssignMission';
import { MissionId } from 'modules/missions/types';
import type { FC } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { NotificationProvider, notificationsMap } from './3rdParty';

export const NotificationsContext = createContext<{}>(null as any);

export const NotificationsContextProvider: FC = ({ children }) => {
  const { user } = useContext(AuthContext);
  const { isB: isShowQuests } = useABTestingV2({
    experiment: AB_TESTS.QUESTS,
  });
  const { requestAssignMission } = useAssignMission();

  const { path, identifyUser, resetUser, trackEvent } =
    window.appConfig.APP_ENV === 'development'
      ? notificationsMap[NotificationProvider.None]
      : notificationsMap[NotificationProvider.CustomerIO];

  const isAssignMission = useMemo(
    () => canAssignMission(user, isShowQuests, MissionId.WN_3),
    [isShowQuests, user]
  );

  useEffect(() => {
    if (user?.id == null) return;

    identifyUser(user.id);

    return () => {
      resetUser();
    };
  }, [user, identifyUser, resetUser]);

  useEffect(() => {
    if (!path) return;

    const script = createScript(path);

    script.onload = () => {
      const isStandalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;

      if (isStandalone) {
        trackEvent(CustomerIOEvents.PWA_OPENED);
        if (isAssignMission && user) {
          requestAssignMission(MissionId.WN_3);
        }
      }
    };

    return () => {
      script.remove();
    };
  }, [isAssignMission, path, requestAssignMission, trackEvent, user]);

  return (
    <NotificationsContext.Provider value={{}}>
      {children}
    </NotificationsContext.Provider>
  );
};
